import React from 'react';
import { Link } from 'react-router-dom';

import PageLayout from '@/components/layout/PageLayout';
import { PermissionGuard } from '@/components/layout';
import { PrimaryButton } from '@/components/ui/buttons/PrimaryButton';
import { IconScout } from '@/components/ui/images/IconScout';
import { ROUTE_DEFINITIONS } from '@/routes';
import Table from '@/components/ui/tables/Table';
import { IndicatorCard } from '@/components/ui/cards/IndicatorCard';
import { ExportDropdown } from '@/components/ui/filters/ExportDropdown';
import { FilterDropdown } from '@/components/ui/filters/FilterDropdown';
import { useMonthlyCutsList } from '@/hooks/attendance/monthlyCuts/useMonthlyCutsList';
import { useMonthlyCutsExport } from '@/hooks/attendance/monthlyCuts/useMonthlyCutsExport';
import { SendMonthlyCutsToPayrollModal } from '@/components/features/monthlyCuts/SendMonthlyCutsToPayrollModal';
import { useTranslation } from '@/i18n';


export const MonthlyCutsListPage: React.FC = () => {
  const { t: translate } = useTranslation();
  const {
    rows,
    total,
    page,
    pageSize,
    setPage,
    setPageSize,
    columns,
    onSearch,
    onSortChange,
    searchText,
    sort,
    loading,
    counters,
    filterConfig,
    filters,
    selectedFilters,
    onChangeFilter,
    canView,
    permissionsLoaded,
    selectable,
    bulkActions,
    onSelectionChange,
    tableSelectionKey,
    isSendPayrollModalOpen,
    sendPayrollCuts,
    onSendPayrollModalClose,
    onSendPayrollSuccess,
  } = useMonthlyCutsList();

  const { onExport, isExporting } = useMonthlyCutsExport({
    searchText,
    sort,
    filterCompanyId: selectedFilters.companyId,
    year: selectedFilters.year,
    month: selectedFilters.month,
    state: selectedFilters.state,
  });

  if (permissionsLoaded && !canView) return null;

  return (
    <PageLayout>
      <div className="flex flex-col gap-6" data-testid="monthly-cuts-list-page">

        {/* Counters */}
        <div className="flex gap-4">
          <IndicatorCard
            label={translate('attendance:monthlyCutsList.counters.total')}
            value={counters.total}
            variant="default"
            data-testid="indicator-total"
          />
          <IndicatorCard
            label={translate('attendance:monthlyCutsList.counters.open')}
            value={counters.open}
            variant="default"
            data-testid="indicator-open"
          />
          <IndicatorCard
            label={translate('attendance:monthlyCutsList.counters.closed')}
            value={counters.closed}
            variant="default"
            data-testid="indicator-closed"
          />
        </div>

        {/* Actions bar */}
        <div className="flex items-center justify-between">
          <PermissionGuard action="create">
            <Link to={ROUTE_DEFINITIONS.CORTE_MENSUAL_CREAR.path}>
              <PrimaryButton iconLeft={<IconScout name="plus" />} data-testid="monthly-cuts-create-button">
                {translate('attendance:monthlyCutsList.actions.create')}
              </PrimaryButton>
            </Link>
          </PermissionGuard>

          <PermissionGuard action="view" fallback="hidden">
            <ExportDropdown
              onExport={onExport}
              isLoading={isExporting}
              placement="right"
              availableFormats={['EXCEL']}
            />
          </PermissionGuard>
        </div>

        {/* Filters */}
        <div className="flex items-center">
          <FilterDropdown
            filters={filterConfig}
            values={filters}
            onChangeFilter={onChangeFilter}
            placement="left"
            data-testid="monthly-cuts-filters"
          />
        </div>

        {/* Table */}
        <Table
          key={tableSelectionKey}
          columns={columns}
          data={rows}
          totalRows={total}
          searchable
          onSearch={onSearch}
          serverSideSort
          onSortChange={onSortChange}
          initialSort={{ key: 'id', direction: 'desc' }}
          selectable={selectable}
          onSelectionChange={onSelectionChange}
          bulkActions={bulkActions}
          pagination={{
            page,
            pageSize,
            total,
            onChange: setPage,
            onPageSizeChange: setPageSize,
          }}
          data-testid="monthly-cuts-table"
          isLoading={loading}
        />
      </div>

      {/* Envío a remuneraciones: oculto sin permiso de edición (ASIS-4971 / FE-012) */}
      <PermissionGuard action="edit" fallback="hidden">
        <SendMonthlyCutsToPayrollModal
          isOpen={isSendPayrollModalOpen}
          selectedCuts={sendPayrollCuts}
          onClose={onSendPayrollModalClose}
          onCompleted={onSendPayrollSuccess}
        />
      </PermissionGuard>
    </PageLayout>
  );
};

export default MonthlyCutsListPage;
